import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  InputGroup,
  Row
} from "react-bootstrap";
import { TableColumn } from "react-data-table-component";
import { BiSolidPencil } from "react-icons/bi";
import { FaRegTrashAlt } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import addico from "../../assets/images/add.svg";
import branchico from "../../assets/images/branch.svg";
import crossico from "../../assets/images/close.svg";
import searchico from "../../assets/images/search.svg";
import { ESDataTable } from "../../controls/ESDataTable";
import ESTooltip from "../../controls/ESTooltip";
import ESAutocompleteSearch from "../../controls/ESAutocompleteSearch";
import {
  deleteSurveySectionQuestions,
  getAllSurveySectionQuestions,
  getSurveySectionMarks,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import { handlePaginationAfterDelete, PageRecordLimit, setPageRecordLimit } from "../../utils/common";
import { showToast, ToastTypeCode } from "../../utils/Constant";
import Loader from "../loader/Loader";
import ConfirmationPopup from "../popUp/ConfirmationPopup";
import AddSurveySectionQuestion from "./AddSurveySectionQuestion";
import SurveySectionQuestionBranchLogic from "./SurveySectionQuestionBranchLogic";
import "./SurveySectionQuestionList.scss";

interface SurveySectionQuestion {
  progressPending?: boolean;
  SurveySectionId: number;
  SurveySectionQuestionId: number;
  QuestionTitle: string;
  QuestionInstructions: string;
  IsRequired: boolean;
  SequenceNumber: number;
  IsActive: number;
  HasBranchLogic: number;
  QuestionId: number;
  ResponseMarks: number;
}

interface CustomTableColumn<T> extends TableColumn<T> {
  onClick?: () => void;
}

interface SurveySectionQuestionListProps {
  sectionId: number;
  onEdit: (id: any) => Promise<void>;
  isDeleted: boolean;
  isDemography?: boolean;
  surveyFormId: any;
  organizationId: number | null | undefined;
  showFormProp: boolean;
  routeFlag: number;
}

const SurveySectionQuestionList: React.FC<SurveySectionQuestionListProps> = ({
  sectionId,
  onEdit,
  isDeleted,
  isDemography,
  surveyFormId,
  organizationId,
  showFormProp,
  routeFlag,
}) => {
  const [surveySectionQuestions, setSurveySectionQuestions] = useState<
    SurveySectionQuestion[]
  >([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(
    sectionId
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PageRecordLimit);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState<string>("SequenceNumber");
  const [sortDir, setSortDir] = useState<string>("ASC");
  const [searchText, setSearchText] = useState<string>("");
  const [showPopup, setShowPopup] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [rowQuestionId, setRowQuestionId] = useState<number>(0);
  const [rowSectionQuestionId, setRowSectionQuestionId] = useState<number>(0);
  const [TotalMarks, setTotalMarks] = useState<number>(0);
  const [isPopupShow, setIsPopupShow] = useState(false);
  const [SurveySectionMark, setSurveySectionMark] = useState<number>(0);
  const [showSearchBox, setShowSearchBox] = useState(true);
  const location = useLocation();

  const fetchSurveySectionQuestions = async (sectionId: number | null, searchOverride?: string) => {
    if (sectionId !== null) {
      // Database fetch
      setLoading(true);
      setError(null);
      try {
        const response: any = await axiosPost(getAllSurveySectionQuestions, {
          SearchText: searchOverride !== undefined ? searchOverride : searchText,
          IsActive: true,
          CurrentPageNo: searchOverride !== undefined ? 1 : currentPage,
          PageSize: pageSize,
          SortDir: sortDir,
          SortBy: sortBy,
          SurveySectionId: (sectionId ?? 0) > 0 ? sectionId : 0, // Don't send negative IDs to backend
        });
        const data = response.data.ExtraData;
        setSurveySectionQuestions(data);
        setTotalRecords(data?.length > 0 ? data[0].TotalRecords : 0);
      } catch (error) {
        setError("Error fetching data");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isDeleted) {
      fetchSurveySectionQuestions(selectedSectionId);
      fetchSurveySectionQuestionBasedOnSection(selectedSectionId, surveyFormId);
    }
  }, [isDeleted, selectedSectionId]);

  const handleEdit = (id: number) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setEditId(id);
    setShowForm(true);
  };

  useEffect(() => {
    if (showFormProp) {
      setShowForm(true);
    } else {
      setShowForm(false);
    }
  }, [showFormProp]);

  const handleAllowBranchLogic = (id: number, questionId: number) => {
    setRowQuestionId(questionId);
    setRowSectionQuestionId(id);
    setIsPopupShow(true);
  };

  const handleCreate = () => {
    setEditId(null);
    setShowForm(true);
    setShowSearchBox(false);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setShowSearchBox(true);
    fetchSurveySectionQuestions(selectedSectionId);
    fetchSurveySectionQuestionBasedOnSection(selectedSectionId, surveyFormId);
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      const response: any = await axiosPost(deleteSurveySectionQuestions, {
        SurveySectionQuestionId: id,
        OrganizationId: organizationId ? organizationId : 0,
      });
      if (response?.data?.Status === 200) {
        setSurveySectionQuestions((prevState) =>
          prevState.filter((item) => item.SurveySectionQuestionId !== id)
        );
        showToast(
          "Form section question deleted successfully",
          ToastTypeCode?.SUCCESS
        );
        handlePaginationAfterDelete(
          response?.data?.Status,
          pageSize,
          currentPage,
          setCurrentPage,
          totalRecords
        );
      } else {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
      setShowPopup(false);
      fetchSurveySectionQuestions(selectedSectionId);
      fetchSurveySectionQuestionBasedOnSection(selectedSectionId, surveyFormId);
    } catch (error) {
      showToast("Error deleting Form section question", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSort = (column: any, sortDirection: "asc" | "desc") => {
    if (Object.keys(column)?.length === 0 && column.constructor === Object) {
      return;
    }
    setSortBy(column.sortField);
    setSortDir(sortDirection.toUpperCase());
  };

  const handleSearchTextChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchText(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearch = (searchOverride?: string) => {
    setCurrentPage(1);
    fetchSurveySectionQuestions(selectedSectionId, searchOverride);
  };

  const handlePerRowsChange = async (newPerPage: any, page: any) => {
    setPageSize(newPerPage);
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowPopup(true);
  };

  useEffect(() => {
    setSelectedSectionId(sectionId);
  }, [sectionId]);

  useEffect(() => {
    fetchSurveySectionQuestions(selectedSectionId);
    fetchSurveySectionQuestionBasedOnSection(selectedSectionId, surveyFormId);
    setPageRecordLimit(pageSize);
  }, [currentPage, pageSize, sortBy, sortDir, selectedSectionId]);

  const handlepopupSHowHide = (newValue: any) => {
    setIsPopupShow(newValue);
    if (newValue === false) {
      fetchSurveySectionQuestions(selectedSectionId);
    }
  };

  const handleSequenceChange = (id: number, newValue: number) => {
    setSurveySectionQuestions((prevState) =>
      prevState?.map((item) =>
        item.SurveySectionQuestionId === id
          ? { ...item, SequenceNumber: newValue }
          : item
      )
    );
  };

  const fetchSurveySectionQuestionBasedOnSection = async (
    sectionId: number | null,
    surveyFormId: number | null
  ) => {
    try {
      const response: any = await axiosPost(getSurveySectionMarks, {
        SurveySectionId: sectionId,
        SurveyFormId: surveyFormId,
      });
      const data = response.data.ExtraData;
      setSurveySectionMark(response.data.ExtraData.TotalMarks);
      setTotalMarks(response.data.ExtraData.GrandTotalMarks);
    } catch (error) {
      setError("Error fetching data");
    }
  };

  const columns: CustomTableColumn<SurveySectionQuestion>[] = [
    {
      name: "Actions",
      cell: (row) => (
        <>
          <ESTooltip text="Delete Question" placement="top">
            <button
              onClick={() => confirmDelete(row.SurveySectionQuestionId)}
              className="btnicon"
            > <FaRegTrashAlt color="#ff3610" size={14} />
            </button>
          </ESTooltip>
          {row.HasBranchLogic === 1 && (
            <ESTooltip text="Add Branching Questions" placement="top">
              <button
                onClick={() =>
                  handleAllowBranchLogic(
                    row.SurveySectionQuestionId,
                    row.QuestionId
                  )
                }
                className="btnicon"
              >
                <img src={branchico} alt="Branch" />
              </button>
            </ESTooltip>
          )}
        </>
      ),
    },
    {
      name: "Question",
      cell: (row) => (
        <ESTooltip text={row.QuestionTitle} placement="top">
          <span
            style={{
              display: "inline-block",
              width: "160px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.QuestionTitle}
          </span>
        </ESTooltip>
      ),
      sortable: true,
      sortField: "QuestionTitle",
    },
    {
      name: "Question Instruction",
      selector: (row) => row.QuestionInstructions,
      sortable: true,
      sortField: "QuestionInstruction",
    },
  ];

  return (
    <>
      <Row>
        <Row>
          <Col className="page-title">
            {TotalMarks !== 0 && (
              <>
                <span style={{ fontWeight: "bold" }}>Total Marks: </span>
                <span style={{ color: "#007bff", fontWeight: "bold" }}>
                  {TotalMarks}
                </span>{" "}
                <span style={{ fontWeight: "bold" }}>Section Marks: </span>
                <span style={{ color: "#28a745", fontWeight: "bold" }}>
                  {SurveySectionMark}
                </span>
              </>
            )}
          </Col>
        </Row>

        <Col md={6}>
          <h2 className="page-title">
            {location.state?.routeFlag === 2
              ? "Quiz Questions"
              : "Form Questions"}
          </h2>
        </Col>
        <Col md={6}>
          <div className="search-surveyform">
            {showSearchBox && (
              <div style={{ flexGrow: 1, minWidth: '200px' }}>
                <ESAutocompleteSearch
                  apiUrl={getAllSurveySectionQuestions}
                  labelKey="QuestionTitle"
                  valueKey="SurveySectionQuestionId"
                  initialValue={searchText}
                  onSearchChange={(text) => setSearchText(text)}
                  onSelect={(item) => {
                    setSearchText(item.QuestionTitle);
                    handleSearch(item.QuestionTitle);
                  }}
                  onSubmit={() => handleSearch()}
                  otherParams={{
                    IsActive: true,
                    SurveySectionId: (selectedSectionId ?? 0) > 0 ? selectedSectionId : 0,
                  }}
                />
              </div>
            )}
            <ESTooltip
              text={"Edit Section Questions"}
              placement="top"
            >
              <Button
                onClick={() => handleEdit(sectionId)}
                className="primary-btn btn-add"
                style={{ marginRight: "5px" }}
              >
                <BiSolidPencil color="white" size={14} />
              </Button>
            </ESTooltip>
            <ESTooltip
              text={
                showForm
                  ? "Close"
                  : location.state?.routeFlag === 2
                    ? "Add Question in Quiz Section"
                    : "Add Question in Form Section"
              }
              placement="top"
            >
              <Button
                onClick={showForm ? handleFormClose : handleCreate}
                className="primary-btn btn-add"
              >
                {showForm ? (
                  <img src={crossico} className="closeico" alt="close" />
                ) : (
                  <img src={addico} alt="add" />
                )}
              </Button>
            </ESTooltip>
          </div>
        </Col>
      </Row>
      {showForm && (
        <AddSurveySectionQuestion
          isDemography={isDemography}
          sectionId={sectionId}
          onClose={handleFormClose}
          editId={editId}
          surveyFormId={surveyFormId}
          organizationId={organizationId}
          routeFlag={routeFlag}
        />
      )}

      {!showForm && (
        <div className="questionResponseOptions mt-3">
          <ESDataTable
            data={surveySectionQuestions}
            columns={columns}
            paginationTotalRows={totalRecords}
            onChangePage={handlePageChange}
            onSort={handleSort}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        </div>
      )}

      <ConfirmationPopup
        show={showPopup}
        message="Are you sure you want to delete?"
        onConfirm={() => {
          if (deleteId) {
            handleDelete(deleteId);
          }
        }}
        onCancel={() => setShowPopup(false)}
      />
      {isPopupShow && (
        <SurveySectionQuestionBranchLogic
          onUpdateParentValue={handlepopupSHowHide}
          surveySectionId={sectionId}
          rowQuestionId={rowQuestionId}
          rowSectionQuestionId={rowSectionQuestionId}
          organizationId={organizationId}
          surveyFormId={surveyFormId}
        />
      )}
      {loading ? <Loader /> : null}
    </>
  );
};

export default SurveySectionQuestionList;
